/**
 * Thin client for snake2d_gm session APIs.
 * GM is the source of truth for rules/state; FE only renders + sends intents.
 *
 * Coordinate convention:
 *   GM stores (row, col). Canvas uses { x: col, y: row }.
 */

import { API_ENDPOINTS } from '../../../utils/config';
import { acquireGmLease, endGmSession, noteGmState } from '../gmLease';

/** Lease key for this game's shared GM (game_engines/gm_registry.py). */
export const GM_GAME_ID = 'snake2d';

export const SNAKE_GM_BASE = API_ENDPOINTS.SNAKE_GM_BASE;

export const DIR_NAME = {
    '0,-1': 'up',
    '0,1': 'down',
    '-1,0': 'left',
    '1,0': 'right',
};

export const DIR_VEC = {
    up: { x: 0, y: -1 },
    down: { x: 0, y: 1 },
    left: { x: -1, y: 0 },
    right: { x: 1, y: 0 },
};

export function vecToDirection(vec) {
    if (!vec) return null;
    return DIR_NAME[`${vec.x},${vec.y}`] || null;
}

export function directionToVec(name) {
    return DIR_VEC[name] || { x: 0, y: 0 };
}

/** GM (row,col) → canvas {x,y} */
export function cellToCanvas(cell) {
    if (!cell) return { x: 0, y: 0 };
    if (Array.isArray(cell)) return { x: cell[1], y: cell[0] };
    return {
        x: cell.x ?? cell.col ?? 0,
        y: cell.y ?? cell.row ?? 0,
    };
}

export function snakeToCanvas(snake) {
    if (!Array.isArray(snake)) return [];
    return snake.map(cellToCanvas);
}

async function parseJson(res) {
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        const err = new Error(data.error || `Snake GM HTTP ${res.status}`);
        err.status = res.status;
        err.data = data;
        throw err;
    }
    return noteGmState(GM_GAME_ID, data);
}

export async function createSnakeSession({ variableGrid = false } = {}) {
    await acquireGmLease(GM_GAME_ID);
    const res = await fetch(`${SNAKE_GM_BASE}/session/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ variable_grid: Boolean(variableGrid) }),
    });
    return parseJson(res);
}

export async function resetSnakeSession(sessionId, { variableGrid = false } = {}) {
    const res = await fetch(`${SNAKE_GM_BASE}/session/${sessionId}/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ variable_grid: Boolean(variableGrid) }),
    });
    return parseJson(res);
}

export async function moveSnakeSession(sessionId, direction) {
    const res = await fetch(`${SNAKE_GM_BASE}/session/${sessionId}/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ direction }),
    });
    return parseJson(res);
}

export async function fetchSnakeSession(sessionId) {
    const res = await fetch(`${SNAKE_GM_BASE}/session/${sessionId}/info`);
    return parseJson(res);
}

export async function deleteSnakeSession(sessionId) {
    if (!sessionId) return;
    endGmSession(GM_GAME_ID, sessionId);
    try {
        await fetch(`${SNAKE_GM_BASE}/session/${sessionId}/delete`, { method: 'POST' });
    } catch {
        /* best-effort cleanup */
    }
}

/** Apply a GM state payload onto canvas refs / React setters. */
export function applyGmStateToLocal(data, { snakeRef, foodRef, setScore, setDifficultyLevel }) {
    if (!data) return;
    if (Array.isArray(data.snake)) {
        snakeRef.current = snakeToCanvas(data.snake);
    }
    if (data.food != null) {
        foodRef.current = cellToCanvas(data.food);
    }
    if (typeof data.score === 'number') {
        setScore(data.score);
    }
    const boardSize = data.board_size || data.init_state?.board_size || 10;
    const variable = Boolean(data.variable_grid ?? data.init_state?.variable_grid);
    if (variable && typeof setDifficultyLevel === 'function') {
        // level = (board - 10) / 2  ↔  grid_size_for_score
        setDifficultyLevel(Math.max(0, Math.floor((boardSize - 10) / 2)));
    } else if (typeof setDifficultyLevel === 'function') {
        setDifficultyLevel(0);
    }
    return {
        boardSize,
        variableGrid: variable,
        gameOver: Boolean(data.game_over || data.is_game_over),
        score: typeof data.score === 'number' ? data.score : 0,
        foodHistory: Array.isArray(data.food_history) ? data.food_history : [],
    };
}
